/**
 * MDTable Component
 *
 * Purpose: Flexible table component supporting both metadata-driven and manual configurations
 * Architecture: React component with Material-UI DataGrid integration
 * Dependencies: MUI DataGrid, React Query (future), custom types
 * Author: Development Team
 * Created: 2025-01-27
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  DataGridPro,
  GridColDef,
  GridColType,
  GridRowSelectionModel,
  GridSortModel,
  GridFilterModel,
  GridToolbar,
  GridColumnOrderChangeParams,
  GridTreeDataGroupingCell,
  GRID_REORDER_COL_DEF,
} from '@mui/x-data-grid-pro';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Alert,
  AlertTitle,
  CircularProgress,
  Theme,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { MDTableProps, TableColumn, ViewConfiguration, MDMLookupGetValue } from '../../types/table';
import { formatDate } from '../../utils/tableData';
import { metadataService } from '../../services/metadataService';
import { useMDMLookup, useMDMValuesAll } from '../../services/mdmCacheService';
import { useHierarchyAncestorFields } from '../../hooks/useHierarchyAncestorFields';
import { MissingValueIndicator } from '../common';
import MDTableToolbar from './MDTableToolbar';
import { generateColumnsFromView, getFieldValueFromRow } from '../../utils/tableHelpers';

// Slugify an arbitrary string for use as a stable localStorage key fragment.
// Lower-cases, swaps non-alphanumerics for `-`, and collapses/trims hyphens
// so `"Workflow Steps"` → `"workflow-steps"`.
const slugify = (raw: string): string =>
  raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

// Width of the gutter column `collapsibleGroups` keeps at the left edge. A
// group header spans the whole row from this column, so on leaf rows the
// gutter is simply how far the children sit in from their header — the same
// 16px (theme spacing × 2) that DataGridPro's own GridTreeDataGroupingCell
// indents each nested level by (`rowNode.depth * offsetMultiplier`, with the
// default multiplier of 2). Matching it keeps the table reading like a
// standard MUI tree rather than inventing a second indent size.
export const GROUP_GUTTER_WIDTH = 16;

// The reorder column DataGridPro would otherwise inject by itself. Passing it
// explicitly in `columns` is the grid's supported way of choosing where the
// drag handle sits: the row-reorder pre-processor keeps a caller-supplied
// `__reorder__` column where it is, instead of forcing it to the very front.
// That matters with `collapsibleGroups`, whose tree-data gutter is always
// prepended first — so the handle lands just after the gutter, and the group
// header (which spans from the gutter) is the left-most thing on the row.
const REORDER_COLUMN: GridColDef = { ...GRID_REORDER_COL_DEF };

/**
 * Hook to fetch view configuration from metadata service
 */
const useMetadataView = (
  modelId: string | undefined,
  viewType: string | undefined,
  tenantId: string | undefined
) => {
  const [viewConfig, setViewConfig] = useState<ViewConfiguration | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!modelId || !viewType || !tenantId) {
      setViewConfig(null);
      return;
    }

    const fetchViewConfig = async () => {
      setLoading(true);
      setError(null);
      try {
        const config = await metadataService.getModelView(
          modelId,
          tenantId,
          viewType as 'edit-modal' | 'table' | 'list' | 'detail'
        );
        setViewConfig(config);
      } catch (err: any) {
        console.error('Failed to fetch view configuration:', err);
        setError(err.message || 'Failed to fetch view configuration');
      } finally {
        setLoading(false);
      }
    };

    fetchViewConfig();
  }, [modelId, viewType, tenantId]);

  return { viewConfig, loading, error };
};

/**
 * MDTable Component
 *
 * A flexible table component that supports both metadata-driven and manual configurations.
 * Uses Material-UI DataGrid for performance with large datasets.
 */
const MDTable = <T extends Record<string, any>>({
  mode,
  metadata,
  manual,
  data,
  loading = false,
  error = null,
  tenantId: propTenantId,
  selectable = false,
  isRowSelectable,
  sortable = true,
  filterable = true,
  editMode = false,
  pagination: _pagination,
  onRowClick,
  isRowClickable,
  onSelectionChange,
  onBulkAction: _onBulkAction,
  onViewChange: _onViewChange,
  onExport,
  onRefresh,
  onAddNew,
  showAddButton = false,
  showRefreshButton = false,
  customRenderers = {},
  customFilters: _customFilters = {},
  customActions: _customActions = [],
  customToolbarFilters,
  customToolbarActions,
  customHeaderActions,
  selectedIds,
  selectionBar,
  className,
  sx,
  defaultSortModel = [],
  defaultColumnVisibility = {},
  valueChipColorLookup,
  reorderable = false,
  groupByField,
  collapsibleGroups = false,
  groupLabelField,
  groupColumnHeaderName = '',
  defaultGroupsExpanded = true,
  reorderLabelField,
  onRowReorder,
}: MDTableProps<T>) => {
  // Resolve tenantId from props or metadata config
  const tenantId = propTenantId || metadata?.tenantId || '';

  // Collapsible grouping is DataGridPro tree data underneath, and it can only
  // build a tree if it knows what to group by.
  const groupingEnabled = collapsibleGroups && !!groupByField;

  // Row reordering requires the rendered row order to exactly match `data`
  // order (see the `reorderable` prop doc in types/table.ts). MUI DataGridPro
  // itself hard-disables dragging whenever a column sort is active, and the
  // reorder indices it reports are always relative to its own sorted/filtered
  // row list — so sorting and filtering are neutralized at the grid level
  // while reorderable is on, regardless of what the caller passed in.
  const effectiveSortable = sortable && !reorderable;
  const effectiveFilterable = filterable && !reorderable;

  // Fetch view configuration for metadata mode (must be before mdmFields useMemo)
  const {
    viewConfig,
    loading: viewLoading,
    error: viewError,
  } = useMetadataView(metadata?.modelId, metadata?.viewType, metadata?.tenantId);

  // Helper to check if a column needs MDM lookup
  const needsMDMLookup = (col: TableColumn): boolean => {
    return col.datatype === 'list' || col.mdmLookup === true;
  };

  // Extract MDM fields from columns that have datatype: 'list' or mdmLookup: true
  const mdmFields = useMemo(() => {
    // Manual mode: check TableColumn datatype/mdmLookup
    if (mode === 'manual' && manual?.columns) {
      return manual.columns.filter(needsMDMLookup).map((col) => col.field);
    }
    // Metadata mode: check field_definition.datatype from view config
    if (mode === 'metadata' && viewConfig?.fields) {
      return viewConfig.fields
        .filter((field) => {
          // Check multiple possible locations for datatype
          const datatype =
            field.field_definition?.datatype || field.field_datatype || field.datatype;
          return datatype?.toLowerCase() === 'list';
        })
        .map((field) => field.fielddef_id);
    }
    return [];
  }, [mode, manual?.columns, viewConfig?.fields]);

  // For hierarchical fields, also load ancestor field values so non-leaf values resolve.
  const hierarchyAncestorFields = useHierarchyAncestorFields(
    viewConfig?.fields,
    tenantId,
    mode === 'metadata'
  );

  // Combine direct MDM fields with hierarchy ancestor fields
  const allMdmFields = useMemo(() => {
    if (hierarchyAncestorFields.length === 0) return mdmFields;
    const combined = new Set([...mdmFields, ...hierarchyAncestorFields]);
    return Array.from(combined);
  }, [mdmFields, hierarchyAncestorFields]);

  // Get MDM lookup function for all MDM fields (including hierarchy ancestors)
  const { getValue: mdmGetValue, isLoading: mdmLoading } = useMDMLookup(allMdmFields, tenantId);

  // ORCH-191: a currency cell takes its symbol from the row's own currency (fd_currency →
  // the value's ISO code), never from the user's locale, which controls only separators
  // and symbol placement. Only fetched when the view has a currency column.
  const hasCurrencyColumn = useMemo(
    () =>
      mode === 'metadata' &&
      !!viewConfig?.fields?.some((f) => f.field_definition?.datatype?.toLowerCase() === 'currency'),
    [mode, viewConfig]
  );
  const { data: currencyValues } = useMDMValuesAll('fd_currency', tenantId, {
    enabled: hasCurrencyColumn && !!tenantId,
  });
  const currencyCodeForRow = useMemo(() => {
    const codes = new Map<string, string>();
    (currencyValues || []).forEach((cv) => {
      const code = cv.meta_data?.fd_code;
      if (typeof code === 'string' && code) codes.set(cv.id, code);
    });
    return (row: any): string | undefined => {
      const currency = getFieldValueFromRow(row, 'fd_currency');
      return typeof currency === 'string' ? codes.get(currency) : undefined;
    };
  }, [currencyValues]);

  // State management
  // GridRowSelectionModel in v8 uses object format with type and ids
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set<string | number>(),
  });
  const [sortModel, setSortModel] = useState<GridSortModel>(defaultSortModel);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

  // Auto-derive a localStorage key for column-order persistence so every
  // MDTable consumer gets persistence for free — manual-mode tables key off
  // the slugified `manual.title`, metadata-mode tables key off the stable
  // `modelId:viewType` pair (NOT the optional `metadata.title`, which is a
  // user-facing override and not a stable identifier). When neither is
  // available, persistence is skipped and column order lives in memory only.
  const columnOrderStorageKey = useMemo<string | null>(() => {
    if (mode === 'manual' && manual?.title) {
      const slug = slugify(manual.title);
      return slug ? `mdtable:${slug}:column-order` : null;
    }
    if (mode === 'metadata' && metadata?.modelId && metadata?.viewType) {
      return `mdtable:${metadata.modelId}:${metadata.viewType}:column-order`;
    }
    return null;
  }, [mode, manual?.title, metadata?.modelId, metadata?.viewType]);

  // Persists user-driven column reordering across re-renders. Null means "use
  // the natural order from `columns`"; otherwise it's the desired field order.
  // Hydrates from (and is written back to) localStorage under the auto-derived
  // key so the order survives unmount (tab switches) and page reloads.
  const [columnOrder, setColumnOrder] = useState<string[] | null>(() => {
    if (!columnOrderStorageKey || typeof window === 'undefined') return null;
    try {
      const stored = window.localStorage.getItem(columnOrderStorageKey);
      if (!stored) return null;
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.every((f) => typeof f === 'string')) {
        return parsed;
      }
    } catch {
      // Corrupt value — fall through and ignore.
    }
    return null;
  });

  // Sync column visibility with defaultColumnVisibility prop when it changes
  // Use JSON.stringify to compare object contents, not reference, to prevent infinite loops
  const prevDefaultColumnVisibilityRef = useRef<string>('');
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>(
    () => {
      // Initialize with the provided defaultColumnVisibility or empty object
      return defaultColumnVisibility || {};
    }
  );

  useEffect(() => {
    const currentKey = JSON.stringify(defaultColumnVisibility || {});
    if (currentKey !== prevDefaultColumnVisibilityRef.current) {
      prevDefaultColumnVisibilityRef.current = currentKey;
      setColumnVisibilityModel(defaultColumnVisibility || {});
    }
  }, [defaultColumnVisibility]);

  // Helper function to map metadata datatypes to MUI DataGrid types
  const mapDataTypeToGridType = (
    dataType: string | undefined,
    _isMultiselect?: boolean
  ): GridColType | undefined => {
    if (!dataType) {
      return 'string';
    }

    switch (dataType.toLowerCase()) {
      case 'string':
      case 'text':
      case 'varchar':
        return 'string';
      case 'number':
      case 'integer':
      case 'int':
      case 'float':
      case 'decimal':
        return 'number';
      case 'date':
      case 'datetime':
      case 'timestamp':
        return 'date';
      case 'boolean':
      case 'bool':
        return 'boolean';
      case 'list':
      case 'singleselect':
      case 'multilist':
      case 'multiselect':
      case 'multipleSelect':
        return 'singleSelect';
      default:
        return 'string';
    }
  };

  // Helper function to convert TableColumn to GridColDef
  const convertToGridColDef = (column: TableColumn): GridColDef => {
    // Handle nested fields from hierarchy_config
    const getValueGetter = () => {
      // First check if column has explicit valueGetter
      if (column.valueGetter) {
        return column.valueGetter;
      }
      // For hierarchy-related nested fields, extract from hierarchy_config
      if (
        column.field === 'hierarchy_label' ||
        column.field === 'parent_fielddef_id' ||
        column.field === 'child_fielddef_id'
      ) {
        return (_value: any, row: any) => {
          // For MUI DataGrid v7+/v8, valueGetter has the signature (value, row, column, apiRef)
          if (!row) {
            return null;
          }
          return row.hierarchy_config?.[column.field] || null;
        };
      }
      return undefined;
    };

    const valueGetter = getValueGetter();

    // Create the MDM lookup function wrapper for this column
    const getMDMValue: MDMLookupGetValue = (fielddefId: string, uuid: string) => {
      return mdmGetValue(fielddefId, uuid);
    };

    return {
      field: column.field,
      headerName: column.headerName,
      width: column.width,
      flex: column.flex,
      sortable: column.sortable !== false && effectiveSortable,
      filterable: column.filterable !== false && effectiveFilterable,
      resizable: column.resizable !== false, // Enable column resizing (default true unless explicitly disabled)
      type: mapDataTypeToGridType(column.type, column.isMultiselect),
      align: column.align,
      editable: editMode,
      // Add valueGetter for nested fields or custom computed fields
      ...(valueGetter ? { valueGetter } : {}),
      // For singleSelect/multipleSelect, we need to handle valueOptions differently
      // MUI DataGrid expects valueOptions to be passed as a separate property
      ...(column.type === 'singleSelect' || column.type === 'multipleSelect'
        ? { valueOptions: column.valueOptions }
        : {}),
      renderCell: (params: any) => {
        // First check for custom renderers
        const customRenderer = customRenderers[column.field];
        if (customRenderer) {
          return customRenderer(params.value, params.row);
        }

        // Handle MDM lookup columns (datatype: 'list' or mdmLookup: true)
        if (needsMDMLookup(column)) {
          const value = params.value;

          // If custom renderCell provided for MDM column, pass getValue function
          if (column.renderCell) {
            return column.renderCell(value, params.row, getMDMValue);
          }

          // Default MDM rendering
          if (!value) return '-';

          const displayName = mdmGetValue(column.field, value);

          if (displayName) {
            return displayName;
          }

          // If still loading, show the raw UUID while we wait
          if (mdmLoading) {
            return (
              <Box
                component="span"
                sx={{ fontFamily: 'monospace', fontSize: '0.75rem', opacity: 0.6 }}
              >
                {value.substring(0, 8)}...
              </Box>
            );
          }

          // Value not found after loading - show missing indicator
          return <MissingValueIndicator uuid={value} />;
        }

        // Then check for column-specific renderer (non-MDM columns)
        if (column.renderCell) {
          return column.renderCell(params.value, params.row);
        }

        // Default rendering based on column type
        if (column.type === 'date' || column.type === 'dateTime') {
          return params.value ? formatDate(new Date(params.value)) : '';
        }

        return params.value;
      },
    };
  };

  // Which column carries the record link (ORCH-225): the first one that is
  // not a leading `isOrderColumn` ordinal. Only manual columns can declare an
  // ordinal; in metadata mode the first column is the link.
  const orderColumnFields = useMemo(
    () => new Set((manual?.columns ?? []).filter((c) => c.isOrderColumn).map((c) => c.field)),
    [manual?.columns]
  );
  const recordLinkIndex = useCallback(
    (cols: GridColDef[]) => cols.findIndex((c) => !orderColumnFields.has(c.field)),
    [orderColumnFields]
  );

  // Column generation based on mode
  const columns = useMemo((): GridColDef[] => {
    try {
      let cols: GridColDef[] = [];

      if (mode === 'manual' && manual?.columns) {
        // Convert our TableColumn interface to MUI GridColDef
        cols = manual.columns.map(convertToGridColDef);
      } else if (mode === 'metadata' && metadata && viewConfig) {
        // Metadata mode - combine columnsBeforeRelatedFields, dynamic columns, and columnsAfterRelatedFields
        // Pass MDM lookup function for automatic list field resolution
        const dynamicColumns = generateColumnsFromView(
          viewConfig,
          customRenderers,
          valueChipColorLookup,
          mdmGetValue,
          mdmLoading,
          currencyCodeForRow
        );
        const beforeColumns = metadata.columnsBeforeRelatedFields || [];
        const afterColumns = metadata.columnsAfterRelatedFields || [];
        cols = [...beforeColumns, ...dynamicColumns, ...afterColumns];
      } else if (mode === 'metadata' && metadata && !viewConfig && !viewLoading) {
        // If we're in metadata mode but don't have a view config and we're not loading,
        // this might be an error condition - log it but don't crash
        console.warn('MDTable: Metadata mode but no viewConfig available', {
          mode,
          metadata,
          viewConfig,
          viewLoading,
          viewError,
        });
      }

      // The one "open this record" pattern for every list table (ORCH-225):
      // with `onRowClick`, the record-link column — the first column, or the
      // first after any leading `isOrderColumn` ordinal (Workflow Steps'
      // Order) — opens the row on click or Enter. It reads as normal table
      // text at rest and takes the breadcrumbs' link style on hover and
      // keyboard focus: underline plus the theme's primary colour (ORCH-425).
      // Callers put the entity name there and never draw their own link or
      // bold it. `isRowClickable` leaves rows with no record to open as text.
      const linkIndex = onRowClick ? recordLinkIndex(cols) : -1;
      if (linkIndex >= 0 && onRowClick) {
        const linkColumn = cols[linkIndex];
        const originalRenderCell = linkColumn.renderCell;

        // The ordinal(s) and the link can't be dragged elsewhere: the name
        // is the first data column on every table (see `orderedColumns`).
        for (let i = 0; i <= linkIndex; i += 1) {
          cols[i] = { ...cols[i], disableReorder: true };
        }

        cols[linkIndex] = {
          ...linkColumn,
          disableReorder: true,
          renderCell: (params: any) => {
            const cellContent = originalRenderCell ? (
              originalRenderCell(params)
            ) : (
              <Typography variant="body2">{params.value || ''}</Typography>
            );

            if (isRowClickable && !isRowClickable(params.row)) {
              return cellContent;
            }

            const open = () => onRowClick(params.row);
            return (
              <Box
                role="link"
                tabIndex={0}
                data-testid="md-table-record-link"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent row click and other event bubbling
                  open();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.stopPropagation(); // Keep the grid's own Enter handling out
                    open();
                  }
                }}
                onMouseDown={(e) => {
                  e.stopPropagation(); // Prevent row selection on click
                }}
                sx={{
                  cursor: 'pointer',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  // Nothing at rest: the name reads as normal table text. On
                  // hover and keyboard focus it takes the breadcrumbs' link
                  // style (TopBanner's BreadcrumbLink: underline="hover" and
                  // the primary colour). Flex children don't inherit
                  // text-decoration, so the caller's Typography is styled
                  // directly as well as bare text.
                  '&:hover, &:focus-visible': {
                    color: 'primary.main',
                    textDecoration: 'underline',
                  },
                  '&:hover .MuiTypography-root, &:focus-visible .MuiTypography-root': {
                    color: 'primary.main',
                    textDecoration: 'underline',
                  },
                  // The underline and colour are the focus indicator; no
                  // ring on top of the grid's own cell focus (ORCH-417)
                  '&:focus-visible': {
                    outline: 'none',
                  },
                  '&:active': {
                    opacity: 0.8,
                  },
                }}
              >
                {cellContent}
              </Box>
            );
          },
        };
      }

      // Group header rows are generated by DataGridPro and carry none of our
      // fields, but the grid still renders every column for them. A caller's
      // renderCell would happily draw Edit/Delete buttons, a status chip, or
      // the clickable-first-column wrapper onto a header row — controls that
      // act on a row that doesn't exist. Columns without a renderCell need no
      // guard: their value is undefined on a header row, so they render empty.
      if (groupingEnabled) {
        for (let i = 0; i < cols.length; i += 1) {
          const originalRenderCell = cols[i].renderCell;
          if (!originalRenderCell) continue;
          cols[i] = {
            ...cols[i],
            renderCell: (params: any) =>
              params.row?.id === undefined ? null : originalRenderCell(params),
          };
        }
      }

      return cols;
    } catch (error) {
      console.error('MDTable: Error generating columns:', error, {
        mode,
        metadata,
        manual,
        viewConfig,
        viewLoading,
        viewError,
      });
      // Return empty columns array to prevent crash, but log the error
      return [];
    }
  }, [
    mode,
    manual?.columns,
    metadata,
    viewConfig,
    customRenderers,
    onRowClick,
    isRowClickable,
    viewLoading,
    viewError,
    valueChipColorLookup,
    mdmGetValue,
    mdmLoading,
    currencyCodeForRow,
    effectiveSortable,
    effectiveFilterable,
    groupingEnabled,
    recordLinkIndex,
  ]);

  // Apply the user's manual column reordering on top of the natural column
  // order. Without this, dragging a column reorders it inside DataGridPro's
  // internal state but the next re-render (e.g. triggered by clicking a
  // column header to sort) overwrites it with the original `columns` prop.
  //
  // The record link stays the first data column whatever order was saved
  // (ORCH-225): any leading order columns and the link column keep their
  // natural position, and the saved order applies to the columns after them.
  // A saved order that predates this rule (or a drop at index 0) therefore
  // can't push the name off the front of the table.
  const orderedColumns = useMemo((): GridColDef[] => {
    if (!columnOrder || columnOrder.length === 0) {
      return columns;
    }
    const pinnedCount = onRowClick ? recordLinkIndex(columns) + 1 : 0;
    const ordered: GridColDef[] = columns.slice(0, pinnedCount);
    const byField = new Map(columns.slice(pinnedCount).map((c) => [c.field, c]));
    for (const field of columnOrder) {
      const col = byField.get(field);
      if (col) {
        ordered.push(col);
        byField.delete(field);
      }
    }
    // Append any columns not present in the saved order (e.g. metadata
    // added a new column since the user last reordered).
    byField.forEach((col) => ordered.push(col));
    return ordered;
  }, [columns, columnOrder, onRowClick, recordLinkIndex]);

  const handleColumnOrderChange = useCallback(
    (params: GridColumnOrderChangeParams) => {
      // Idempotent: "place `field` at index `targetIndex`" produces the same
      // result no matter how many times it runs against the same inputs. The
      // earlier splice-by-oldIndex version was not idempotent — under any
      // repeated invocation of the updater (React 18 strict/concurrent
      // semantics, batched event handling) the second pass re-spliced against
      // the already-reordered `prev` and effectively undid the drag, which is
      // what made the drag appear to persist in the moment but vanish as soon
      // as the component re-rendered or remounted (e.g. on a tab switch).
      const targetField = params.column.field;
      const targetIndex = params.targetIndex;
      setColumnOrder((prev) => {
        const base = prev ?? orderedColumns.map((c) => c.field);
        if (!base.includes(targetField)) {
          return prev;
        }
        const without = base.filter((f) => f !== targetField);
        const clampedIndex = Math.max(0, Math.min(targetIndex, without.length));
        const next = [
          ...without.slice(0, clampedIndex),
          targetField,
          ...without.slice(clampedIndex),
        ];
        // Short-circuit no-op updates so React can bail out of the re-render.
        if (prev && next.length === prev.length && next.every((f, i) => f === prev[i])) {
          return prev;
        }
        return next;
      });
    },
    [orderedColumns]
  );

  // What DataGridPro actually receives. While reorderable, the drag-handle
  // column goes in explicitly (see `REORDER_COLUMN`) so its position is ours
  // to decide; it is not part of the user-reorderable set, so it stays out of
  // `orderedColumns` and the persisted column order.
  const gridColumns = useMemo(
    (): GridColDef[] => (reorderable ? [REORDER_COLUMN, ...orderedColumns] : orderedColumns),
    [reorderable, orderedColumns]
  );

  // Persist the in-memory column order to localStorage so it survives
  // unmount (tab switches) and full page reloads. Separated from the state
  // updater on purpose — running a side effect inside a React set-function
  // updater is not safe in strict/concurrent mode.
  useEffect(() => {
    if (!columnOrderStorageKey || typeof window === 'undefined') return;
    if (columnOrder === null) return;
    try {
      window.localStorage.setItem(columnOrderStorageKey, JSON.stringify(columnOrder));
    } catch {
      // Quota exceeded or storage disabled — the in-memory order still
      // applies for the current mount.
    }
  }, [columnOrder, columnOrderStorageKey]);

  // Update columnVisibilityModel when columns with hide property change
  useEffect(() => {
    if (mode === 'manual' && manual?.columns) {
      const hiddenColumns: Record<string, boolean> = {};
      manual.columns.forEach((col) => {
        if (col.hide === true) {
          hiddenColumns[col.field] = true;
        }
      });
      if (Object.keys(hiddenColumns).length > 0) {
        setColumnVisibilityModel((prev) => ({
          ...prev,
          ...hiddenColumns,
        }));
      }
    }
  }, [mode, manual?.columns]);

  // Table title and description
  const tableTitle = useMemo(() => {
    if (mode === 'manual') {
      return manual?.title || 'Data Table';
    }
    if (mode === 'metadata' && metadata) {
      // Custom title, then the view's name. Never the model id / view type:
      // those are internal identifiers users shouldn't see (ORCH-421).
      return metadata.title || viewConfig?.view_name || 'Data Table';
    }
    return 'Data Table';
  }, [mode, manual?.title, metadata, viewConfig]);

  const tableDescription = useMemo(() => {
    if (mode === 'manual') {
      return manual?.description;
    }
    return undefined;
  }, [mode, manual?.description]);

  // A controlled selection replaces the grid's own whenever the caller changes it
  useEffect(() => {
    if (selectedIds) {
      setSelectedRows({ type: 'include', ids: new Set<string | number>(selectedIds) });
    }
  }, [selectedIds]);

  // Event handlers
  const handleRowSelectionChange = useCallback(
    (newSelection: GridRowSelectionModel) => {
      setSelectedRows(newSelection);
      if (onSelectionChange) {
        // Convert v8 selection model to array for backward compatibility
        if (newSelection && typeof newSelection === 'object' && 'ids' in newSelection) {
          const selectedIds = Array.from(newSelection.ids).map((id) => String(id));
          onSelectionChange(selectedIds);
        } else {
          // Fallback for array format (if still supported)
          const selectionArray = Array.isArray(newSelection) ? newSelection : [];
          onSelectionChange(selectionArray.map((id) => String(id)));
        }
      }
    },
    [onSelectionChange]
  );

  const handleSortModelChange = useCallback((newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
  }, []);

  const handleFilterModelChange = useCallback((newFilterModel: GridFilterModel) => {
    setFilterModel(newFilterModel);
  }, []);

  const handleExport = useCallback(
    (format: 'csv' | 'excel') => {
      if (onExport) {
        onExport(format);
      }
    },
    [onExport]
  );

  // Row ids that start a new group (by `groupByField`), used to draw a
  // divider between groups. Relies on `data` already being in group-sorted
  // order — the same guarantee that makes the reorder splice below safe.
  // Not needed with `collapsibleGroups`: there the group header row is the
  // separator, so a divider on top of it would just be noise.
  const groupBoundaryRowIds = useMemo(() => {
    const boundaries = new Set<string | number>();
    if (!reorderable || !groupByField || groupingEnabled) {
      return boundaries;
    }
    let previousGroup: any;
    data.forEach((row: any, index) => {
      const currentGroup = row[groupByField];
      if (index > 0 && currentGroup !== previousGroup) {
        boundaries.add(row.id);
      }
      previousGroup = currentGroup;
    });
    return boundaries;
  }, [reorderable, groupByField, groupingEnabled, data]);

  const getRowClassName = useCallback(
    (params: { id: string | number; row?: any }) => {
      // Tree-data group headers are rows DataGridPro generates itself; their
      // row model is keyed by an internal symbol and carries none of our
      // fields. MDTable's `getRowId` contract is `row => row.id`, so every
      // real row has an `id` and only a generated group header lacks one.
      if (groupingEnabled && params.row?.id === undefined) {
        return 'mdtable-group-row';
      }
      return groupBoundaryRowIds.has(params.id) ? 'mdtable-group-boundary' : '';
    },
    [groupingEnabled, groupBoundaryRowIds]
  );

  // Display label per group key, used for the group header text. Groups stay
  // keyed by `groupByField` even when a label is shown, so two groups sharing
  // a label don't collapse into one (which would let a reorder cross what the
  // user sees as a boundary).
  const groupLabelByKey = useMemo(() => {
    const labels = new Map<string, string>();
    if (!groupingEnabled) {
      return labels;
    }
    data.forEach((row: any) => {
      const key = String(row[groupByField as string]);
      if (!labels.has(key)) {
        labels.set(key, String(row[groupLabelField as string] ?? key));
      }
    });
    return labels;
  }, [groupingEnabled, groupByField, groupLabelField, data]);

  // Two-level tree: [group key, row id]. DataGridPro generates one group node
  // per distinct first segment and hangs the rows off it, which is what gives
  // us the expand/collapse toggle for free.
  const getTreeDataPath = useCallback(
    (row: any) => [String(row[groupByField as string]), String(row.id)],
    [groupByField]
  );

  // The grid's tree-data column, reshaped into a narrow left-edge gutter. A
  // group header row spans from here across every column (`colSpan`), so the
  // expand/collapse toggle and the group label are the left-most things on
  // the page and the group's rows read as belonging to it, indented by the
  // gutter width. Keeping the header text in `headerName` (but not drawing
  // it) leaves the column named in the columns panel and for screen readers
  // without putting a label over a 16px gutter.
  const groupingColDef = useMemo(
    () => ({
      headerName: groupColumnHeaderName,
      width: GROUP_GUTTER_WIDTH,
      minWidth: GROUP_GUTTER_WIDTH,
      resizable: false,
      hideable: false,
      renderHeader: () => null,
      // Group header rows are the only rows with an undefined `id` (see
      // `getRowClassName`). Spanning every visible column is safe: the grid
      // clamps the span at the last column.
      colSpan: (_value: unknown, row: any, _column: unknown, apiRef: any) =>
        row?.id === undefined ? apiRef.current.getVisibleColumns().length : undefined,
      // Leaf rows render nothing here: their grouping key is the row id, and
      // MUI's default cell would print that raw UUID in every row.
      renderCell: (params: any) =>
        params.rowNode?.type === 'group' ? (
          <GridTreeDataGroupingCell
            {...params}
            formattedValue={
              groupLabelByKey.get(String(params.rowNode.groupingKey)) ??
              String(params.rowNode.groupingKey ?? '')
            }
          />
        ) : null,
    }),
    [groupColumnHeaderName, groupLabelByKey]
  );

  // DataGridPro's row-reorder cell shows `row.__reorder__` as the drag
  // preview, falling back to the row's `id` when unset — a raw UUID for
  // entities like Workflow Steps. `reorderLabelField` lets the caller pick a
  // human-readable field instead. `data` (not this) is still what
  // `handleRowReorderChange` splices against; this only adds a display field.
  const rows = useMemo(() => {
    if (!reorderable || !reorderLabelField) {
      return data;
    }
    return data.map((row: any) => ({ ...row, __reorder__: row[reorderLabelField] }));
  }, [reorderable, reorderLabelField, data]);

  // Rejects a drop whose source/target rows don't share the same
  // `groupByField` value, so a row can never leave its own group. DataGridPro
  // simply snaps the row back — there's no dead control to land on.
  const handleIsValidRowReorder = useCallback(
    (context: {
      sourceNode: { id: string | number; type?: string; parent?: string | number | null };
      targetNode: { id: string | number; type?: string; parent?: string | number | null };
    }) => {
      if (groupingEnabled) {
        // In tree-data mode the group boundary is the tree itself: a row may
        // only land between siblings of its own group node. Dropping onto a
        // group header, or anywhere inside another group, would reparent the
        // row, so both are refused here rather than writing a cross-group
        // order to the backend.
        return (
          context.sourceNode.type === 'leaf' &&
          context.targetNode.type === 'leaf' &&
          context.sourceNode.parent === context.targetNode.parent
        );
      }
      if (!groupByField) {
        return true;
      }
      const sourceRow = data.find((row: any) => row.id === context.sourceNode.id) as any;
      const targetRow = data.find((row: any) => row.id === context.targetNode.id) as any;
      if (!sourceRow || !targetRow) {
        return true;
      }
      return sourceRow[groupByField] === targetRow[groupByField];
    },
    [groupingEnabled, groupByField, data]
  );

  // Group header rows are DataGridPro's own; dragging one would reorder whole
  // groups, which MDTable has no way to persist. Only leaves get a handle.
  const handleIsRowReorderable = useCallback(
    (params: { rowNode?: { type?: string } }) => params.rowNode?.type !== 'group',
    []
  );

  // DataGridPro reports `oldIndex`/`targetIndex` as positions within the
  // dragged row's parent node's children (see `useGridRowReorder`). Without
  // `collapsibleGroups` that parent is the grid root, whose children are all
  // of `data` in order; with it, the parent is the row's own group. Either
  // way the indices address the rows below, which line up with `data` only
  // because sorting and filtering are neutralized above while `reorderable`
  // is true.
  const handleRowReorderChange = useCallback(
    (params: { row?: any; oldIndex: number; targetIndex: number }) => {
      if (!onRowReorder || params.oldIndex === params.targetIndex) {
        return;
      }
      const scopeKey =
        groupingEnabled && params.row ? params.row[groupByField as string] : undefined;
      const scopeIndices: number[] = [];
      data.forEach((row: any, index) => {
        if (scopeKey === undefined || row[groupByField as string] === scopeKey) {
          scopeIndices.push(index);
        }
      });

      const scopedRows = scopeIndices.map((index) => data[index]);
      if (params.oldIndex < 0 || params.oldIndex >= scopedRows.length) {
        return;
      }
      const [moved] = scopedRows.splice(params.oldIndex, 1);
      scopedRows.splice(params.targetIndex, 0, moved);

      // Write the re-ordered group back into the slots it already occupied so
      // every other group keeps its position in the emitted array.
      const reordered = [...data];
      scopeIndices.forEach((dataIndex, scopeIndex) => {
        reordered[dataIndex] = scopedRows[scopeIndex];
      });
      onRowReorder(reordered);
    },
    [data, onRowReorder, groupingEnabled, groupByField]
  );

  // Error state - check both data error and view error
  if (error || viewError) {
    const errorMessage = error || viewError;
    console.error('MDTable Error:', errorMessage, { error, viewError, mode, metadata, manual });
    return (
      <Paper sx={{ p: 2, ...sx }} className={className}>
        <Alert severity="error">
          <AlertTitle>Table Error</AlertTitle>
          {errorMessage}
          {viewError && (
            <Box sx={{ mt: 1, fontSize: '0.875rem' }}>
              <strong>View Error:</strong> {viewError}
            </Box>
          )}
        </Alert>
      </Paper>
    );
  }

  // Loading state - check both data loading and view loading
  if ((loading && data.length === 0) || (viewLoading && mode === 'metadata')) {
    return (
      <Paper
        sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', ...sx }}
        className={className}
      >
        <CircularProgress />
      </Paper>
    );
  }

  return (
    <Paper sx={{ width: '100%', ...sx }} className={className}>
      {/* Table Header */}
      <Box sx={{ pt: 0.5, pb: 2, px: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box>
            <Typography variant="h6" component="h2">
              {tableTitle}
            </Typography>
            {tableDescription && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {tableDescription}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {showRefreshButton && onRefresh && (
              <Tooltip title="Refresh data">
                <IconButton size="small" onClick={onRefresh} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            )}

            {showAddButton && onAddNew && (
              <Tooltip title="Add new item">
                <IconButton
                  size="small"
                  onClick={onAddNew}
                  color="primary"
                  data-testid="add-new-item-button"
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            )}

            {onExport && (
              <Tooltip title="Export data">
                <IconButton
                  size="small"
                  onClick={() => handleExport('csv')}
                  disabled={data.length === 0}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            )}

            {/* Custom header actions */}
            {customHeaderActions && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {customHeaderActions}
              </Box>
            )}
          </Box>
        </Box>

        {/* Selection Info */}
        {selectedRows &&
          typeof selectedRows === 'object' &&
          'ids' in selectedRows &&
          selectedRows.ids.size > 0 && (
            <Box data-testid="mdtable-selection-info">
              {selectionBar ? (
                selectionBar(Array.from(selectedRows.ids).map((id) => String(id)))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {selectedRows.ids.size} row(s) selected
                </Typography>
              )}
            </Box>
          )}
      </Box>
      {/* Data Grid */}
      <Box
        sx={{
          height: 600,
          width: '100%',
        }}
      >
        <DataGridPro
          rows={rows}
          columns={gridColumns}
          onColumnOrderChange={handleColumnOrderChange}
          loading={loading}
          // Selection
          checkboxSelection={selectable}
          isRowSelectable={
            isRowSelectable ? (params) => isRowSelectable(params.row as T) : undefined
          }
          rowSelectionModel={selectedRows}
          // The header checkbox would otherwise report "everything except ..." with an
          // empty id set, which reads as nothing selected to callers that list ids
          disableRowSelectionExcludeModel
          onRowSelectionModelChange={handleRowSelectionChange}
          // Sorting — forced empty while reorderable so drag stays enabled
          // (DataGridPro disables row dragging outright when a sort is
          // active) and reorder indices stay valid against `data` order.
          sortModel={reorderable ? [] : sortModel}
          onSortModelChange={reorderable ? undefined : handleSortModelChange}
          sortingOrder={effectiveSortable ? ['asc', 'desc'] : []}
          // Filtering — forced empty while reorderable for the same reason.
          filterModel={reorderable ? { items: [] } : filterModel}
          onFilterModelChange={reorderable ? undefined : handleFilterModelChange}
          filterMode="client"
          // Collapsible groups — DataGridPro tree data, one group node per
          // distinct `groupByField` value (see `collapsibleGroups`). The grid
          // prepends its own grouping column, so the final order is
          // [gutter, drag handle (from `gridColumns`), ...data columns].
          treeData={groupingEnabled}
          getTreeDataPath={groupingEnabled ? getTreeDataPath : undefined}
          groupingColDef={groupingEnabled ? groupingColDef : undefined}
          defaultGroupingExpansionDepth={defaultGroupsExpanded ? -1 : 0}
          // Row reordering
          rowReordering={reorderable}
          onRowOrderChange={reorderable ? (handleRowReorderChange as any) : undefined}
          isValidRowReorder={reorderable ? (handleIsValidRowReorder as any) : undefined}
          isRowReorderable={reorderable ? (handleIsRowReorderable as any) : undefined}
          getRowClassName={getRowClassName}
          // Column visibility
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
          // No pagination - use virtualization/infinite scroll instead (Pro feature)
          hideFooter
          // Editing
          editMode={editMode ? 'cell' : undefined}
          isCellEditable={() => editMode}
          // Row interaction - removed: first column is now clickable instead of entire row

          // Styling
          autoHeight={false}
          density="compact"
          disableRowSelectionOnClick={!selectable}
          // Accessibility
          getRowId={(row: any) => row.id}
          // Performance - virtualization is enabled by default in DataGridPro
          rowHeight={52}
          // Column resizing - explicitly enable column resizing
          disableColumnResize={false}
          // Autosize columns (Pro feature)
          autosizeOptions={{
            includeOutliers: true,
            includeHeaders: true,
          }}
          // Toolbar — reorderable always routes through MDTableToolbar (even
          // without customToolbarFilters) so the filter button can be
          // suppressed; GridToolbar has no equivalent hide switch.
          slots={{
            toolbar:
              reorderable || customToolbarFilters
                ? MDTableToolbar
                : effectiveFilterable || effectiveSortable
                  ? GridToolbar
                  : undefined,
          }}
          slotProps={{
            toolbar:
              reorderable || customToolbarFilters
                ? ({
                    showQuickFilter: effectiveFilterable,
                    hideFilterButton: reorderable,
                    customFilters: customToolbarFilters,
                    customToolbarActions: customToolbarActions,
                  } as any)
                : effectiveFilterable || effectiveSortable
                  ? {
                      showQuickFilter: effectiveFilterable,
                    }
                  : undefined,
            row: {
              'data-testid': 'field-definition-row',
            },
          }}
          sx={{
            // Remove default border from DataGrid root
            '& .MuiDataGrid-root': {
              border: 'none',
            },
            // Cell borders - only bottom border to prevent double borders
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid',
              borderTop: 'none',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center', // Vertically center all cell content (including custom renderers)
            },
            // A cell ring reads as "edit me", and these tables don't edit
            // inline. A mouse click leaves a non-editable cell bare; keyboard
            // focus (arrows, Tab) still draws the grid's ring, on the cell or
            // around a focused control inside it. Editable cells keep the
            // grid's default ring (ORCH-417).
            '& .MuiDataGrid-cell:not(.MuiDataGrid-cell--editable):focus, & .MuiDataGrid-cell:not(.MuiDataGrid-cell--editable):focus-within':
              {
                outline: 'none',
              },
            '& .MuiDataGrid-cell:not(.MuiDataGrid-cell--editable):focus-visible, & .MuiDataGrid-cell:not(.MuiDataGrid-cell--editable):has(:focus-visible)':
              {
                outline: (theme: Theme) => `1px solid ${theme.palette.primary.main}`,
                outlineOffset: '-1px',
              },
            // Column header styling - light grey background to distinguish from data rows
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'grey.50',
              borderBottom: 2,
              borderColor: 'divider',
            },
            // Row hover effect
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'action.hover',
            },
            // Divider marking the start of a new reorder group (see
            // `groupByField`) so grouping stays visible while reordering.
            '& .MuiDataGrid-row.mdtable-group-boundary': {
              borderTop: '2px solid',
              borderTopColor: 'divider',
            },
            // DataGridPro draws the drag icon in every reorder cell and only
            // gates the `draggable` attribute on `isRowReorderable`, so a row
            // that can't be dragged (a group header) still shows a handle that
            // does nothing. Hide the affordance wherever the drag isn't live —
            // the whole point of replacing the Up/Down buttons was to stop
            // offering controls that no-op when used.
            '& .MuiDataGrid-rowReorderCell:not(.MuiDataGrid-rowReorderCell--draggable)': {
              visibility: 'hidden',
            },
            // Collapsible group header rows read as headers, not as data.
            '& .MuiDataGrid-row.mdtable-group-row': {
              backgroundColor: 'grey.100',
              fontWeight: 600,
            },
            '& .MuiDataGrid-row.mdtable-group-row:hover': {
              backgroundColor: 'grey.200',
            },
          }}
          showToolbar
        />
      </Box>
      {/* Footer */}
      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {data.length} row(s)
        </Typography>
      </Box>
    </Paper>
  );
};

export default MDTable;
